import type { AgreementType } from '@portal/flows/domain/sa/types';

/**
 * Service-agreement order and agreement types, ported from portal-ui-lib's
 * `service-agreement.types.ts` (the backend's `ServiceAgreementOrder` / `ServiceAgreement`).
 * Dates are ISO strings; format them Danish at render time.
 */

export type { AgreementType };

export const AGREEMENT_TYPE_VALUES: readonly AgreementType[] = [
  'NEW_CAR',
  'USED_CAR',
  'PROLONGING',
  'KM_CATCH_UP',
];

export type OrderStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'CANCELLED';
export const ORDER_STATUS_VALUES: readonly OrderStatus[] = [
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'CANCELLED',
];

export type AgreementStatus = 'ACTIVE' | 'CANCELLED' | 'EXPIRED';
export const AGREEMENT_STATUS_VALUES: readonly AgreementStatus[] = [
  'ACTIVE',
  'CANCELLED',
  'EXPIRED',
];

interface OrderVehicle {
  id: string;
  regNr: string | null;
  firstReg: string | null;
  vehicleSpec?: { make: string; model: string; version: string | null };
  customer?: {
    id: string;
    name: string | null;
    email: string | null;
    phone?: string | null;
    address?: string | null;
    postalCode?: string | null;
    city?: string | null;
  };
}

export interface ServiceAgreementOrder {
  id: string;
  agreementType: AgreementType;
  customerVehicleId: string;
  sellerInitials: string | null;
  regNr: string | null;
  years: number;
  km: number;
  monthlyServiceCost: number;
  monthlyAddonsCost: number;
  monthlyContractCost: number;
  payMonths: number | null;
  specialOffer: boolean;
  orderPdfUrl: string | null;
  acceptedAt: string | null;
  contractDate: string;
  status: OrderStatus;
  lastService: string | null;
  actualKm: number | null;
  currentAgreementExpiration: string | null;
  currentAgreementKm: number | null;
  previousAgreementExpiration: string | null;
  previousAgreementKm: number | null;
  customerVehicle?: OrderVehicle;
  sellerUser?: { id: string; name: string | null; displayName: string | null; email: string };
  /** Set when the order closed a lead: legacy's "Lead" calculator type. */
  convertedLead?: { id: string } | null;
}

export interface ServiceAgreement {
  id: string;
  agreementType: AgreementType;
  customerVehicleId: string;
  startDate: string;
  endDate: string;
  /** The contract's TOTAL km allowance (CLAUDE.md, Mistakes Log #5). */
  kmBought: number;
  status: AgreementStatus;
  compliance?: {
    kmDrivenAtLastCheck: number | null;
    servicesUsed: number;
    servicesTotal: number;
    isCompliant: boolean;
  } | null;
  serviceVisits?: {
    id: string;
    visitDate: string;
    kmAtVisit: number | null;
    serviceNumber: number | null;
    notes: string | null;
  }[];
}

/** `GET /service-agreement-orders`'s `sortBy` allow-list. */
export type OrderSortField = 'customer' | 'contractDate' | 'createdAt';

export interface OrdersQuery {
  agreementType: AgreementType;
  status: OrderStatus | null;
  search: string;
  /** Server-side; `customer` is the customer's name. Null is newest first. */
  sort?: { field: OrderSortField; direction: 'asc' | 'desc' } | null;
  /** 1-based. */
  page: number;
  limit: number;
}

// ── Eligibility rules (`/service-eligibility-configs`) ─────────────────────

export interface EligibilityCondition {
  field: string;
  operator: string;
  value?: string | number | null;
}

export interface EligibilityConditionGroup {
  id: string;
  conditions: EligibilityCondition[];
}

/** Matches when ANY group matches; a group matches when ALL its conditions do. */
export interface EligibilityRule {
  id: string;
  agreementType: AgreementType;
  priority: number;
  conditionGroups: EligibilityConditionGroup[];
}

/** `GET /service-eligibility-configs/resolved`. */
export interface ResolvedEligibilityConfig {
  rules: EligibilityRule[];
  sourceOrgNodeId: string | null;
  /** The owner's name — render this, never the id (CLAUDE.md, Mistakes Log #4). */
  sourceOrgNodeName?: string | null;
  isDefault: boolean;
}
